import type { ContentPage } from "@/lib/content/types";

// CRM › Omni Channels hub.
export const omniChannels: ContentPage = {
  slug: "omni-channels",
  template: "hrmsFeature",
  seo: {
    title: "Omnichannel CRM - Email, Phone, SMS, WhatsApp & Chat | GetBiz CRM",
    description:
      "Talk to customers on every channel from one CRM. GetBiz CRM omni-channel brings email, telephony, social, SMS, WhatsApp, live chat and web forms into a single inbox.",
    keywords: [
      "omnichannel CRM",
      "multichannel CRM software",
      "unified inbox CRM",
      "CRM communication channels",
      "email telephony sms whatsapp CRM",
      "customer communication software",
    ],
    ogImage: "/assets/data/seoimages/omni-channels.webp",
  },
  leaf: {
    hub: true,
    benefits: [
      { t: "One shared inbox", d: "Every channel in a single conversation view." },
      { t: "Full context", d: "Each message linked to the contact and deal." },
      { t: "Reply faster", d: "Respond from the CRM without switching apps." },
      { t: "No missed messages", d: "Auto-route and assign every conversation." },
      { t: "Meet customers anywhere", d: "Email, phone, social, SMS, WhatsApp and chat." },
      { t: "Capture more leads", d: "Web forms create CRM records instantly." },
      { t: "Complete history", d: "Every touch saved on the contact timeline." },
      { t: "Better experience", d: "Consistent, fast replies on every channel." },
    ],
    process: {
      title: "How Omni-Channel Works, Step by Step",
      description: "Bring every customer conversation into one CRM, with full context on every message.",
      steps: [
        { title: "Connect Channels", caption: "Link email, phone, social, SMS, WhatsApp and chat.", image: "/assets/images/img/product/crm/connect-channels.webp" },
        { title: "Unify the Inbox", caption: "See every conversation in one shared view.", image: "/assets/images/img/product/crm/unify-the-inbox.webp" },
        { title: "Auto-Route", caption: "Assign conversations to the right owner.", image: "/assets/images/img/product/crm/auto-route.webp" },
        { title: "Reply in Context", caption: "Respond with full contact and deal history.", image: "/assets/images/img/product/crm/reply-in-context.webp" },
        { title: "Capture & Log", caption: "Web forms and every touch land on the record.", image: "/assets/images/img/product/crm/capture-log.webp" },
      ],
    },
    highlights: {
      title: "Key Highlights of Omni Channels",
      items: [
        { image: "/assets/images/img/product/crm/email-crm.webp", title: "Email inside the CRM.", href: "/crm-software/omni-channels/email/", description: "Send, receive and track email right inside the CRM, with every message saved on the contact record.", checklist: ["Send & receive","Tracked","Templates","On record"] },
        { image: "/assets/images/img/product/crm/chat-whatsapp-crm.webp", title: "Chat on WhatsApp.", href: "/crm-software/omni-channels/whatsapp/", description: "Message customers on WhatsApp from the CRM, with two-way threads linked to the right record.", checklist: ["Chat in CRM","Two-way","Templates","On record"] },
        { image: "/assets/images/img/product/crm/convert-live-chat.webp", title: "Convert visitors with live chat.", href: "/crm-software/omni-channels/live-chat/", description: "Turn website visitors into leads with live chat that syncs every conversation straight to the CRM.", checklist: ["Website chat","Convert visitors","Syncs to CRM","Real-time"] },
      ],
    },
    hero: {
      caption: "Omni Channels",
      title: "Reach Customers on Every Channel, From One CRM",
      description:
        "Email, phone, social, SMS, WhatsApp, live chat and web forms — all connected to GetBiz CRM, so every conversation lands on the right record and no message is ever missed.",
      h1: "caption",
    },
    split: {
      title: "Every Conversation, One Place",
      bullets: [
        { title: "Unified Inbox", description: "All channels in a single, shared conversation view." },
        { title: "Full Context", description: "Every message linked to the contact and deal." },
        { title: "Faster Replies", description: "Respond from the CRM without switching apps." },
        { title: "No Missed Messages", description: "Route and assign conversations automatically." },
      ],
      image: "/assets/images/img/product/crm/omni-channels.webp",
      imageAlt: "GetBiz CRM omnichannel inbox",
    },
    cardsHeading: {
      title: "Every Channel You Need",
      description: "Connect the channels your customers actually use.",
    },
    cards: [
      { title: "Email", href: "/crm-software/omni-channels/email/", description: "Send, receive and track email right inside the CRM.", image: "/assets/images/img/product/crm/email.webp", imageAlt: "CRM email", checklist: ["Send & Receive","Tracked","Templates","On Record"] },
      { title: "Telephony", href: "/crm-software/omni-channels/telephony/", description: "Call, log and record conversations with built-in telephony.", image: "/assets/images/img/product/crm/telephony.webp", imageAlt: "CRM telephony", checklist: ["Click-to-Call","Call Logging","Recording","On Record"] },
      { title: "Social Media", href: "/crm-software/omni-channels/social-media/", description: "Manage social messages and mentions from one place.", image: "/assets/images/img/product/crm/social-media.webp", imageAlt: "CRM social media", checklist: ["Messages","Mentions","One Place","On Record"] },
      { title: "SMS", href: "/crm-software/omni-channels/sms/", description: "Send and automate SMS that lands on the contact record.", image: "/assets/images/img/product/crm/sms.webp", imageAlt: "CRM SMS", checklist: ["Send SMS","Automate","Two-Way","On Record"] },
      { title: "WhatsApp", href: "/crm-software/omni-channels/whatsapp/", description: "Chat with customers on WhatsApp from the CRM.", image: "/assets/images/img/product/crm/whatsapp.webp", imageAlt: "CRM WhatsApp", checklist: ["Chat in CRM","Two-Way","Templates","On Record"] },
      { title: "Live Chat", href: "/crm-software/omni-channels/live-chat/", description: "Convert website visitors with live chat that syncs to CRM.", image: "/assets/images/img/product/crm/live-chat.webp", imageAlt: "CRM live chat", checklist: ["Website Chat","Convert Visitors","Syncs to CRM","Real-Time"] },
      { title: "Webforms", href: "/crm-software/omni-channels/webforms/", description: "Capture leads with forms that create CRM records instantly.", image: "/assets/images/img/product/crm/webform.webp", imageAlt: "CRM webforms", checklist: ["Capture Leads","Instant Records","No-Code","Auto-Routing"] },
    ],
    testimonials: {
      title: "One inbox for every customer",
      description: "See why teams unify their channels with GetBiz CRM.",
    },
    bookDemoTitle: "Bring every conversation into your CRM.",
    relatedBlogs: "slug",
    relatedBlogsKeyword: "what-is-omnichannel-crm, omnichannel-vs-multichannel, omnichannel-customer-experience-guide",
    metaKey: "omni-channels",
  },
  sections: [],
};
